// filepath: src/component/page/poll/redux/pollActions.js
import { pollService } from '../api/pollService';

// Action Types
export const FETCH_POLLS_REQUEST = 'FETCH_POLLS_REQUEST';
export const FETCH_POLLS_SUCCESS = 'FETCH_POLLS_SUCCESS';
export const FETCH_POLLS_FAILURE = 'FETCH_POLLS_FAILURE';

export const CREATE_POLL_REQUEST = 'CREATE_POLL_REQUEST';
export const CREATE_POLL_SUCCESS = 'CREATE_POLL_SUCCESS';
export const CREATE_POLL_FAILURE = 'CREATE_POLL_FAILURE';

export const DELETE_POLL_REQUEST = 'DELETE_POLL_REQUEST';
export const DELETE_POLL_SUCCESS = 'DELETE_POLL_SUCCESS';
export const DELETE_POLL_FAILURE = 'DELETE_POLL_FAILURE';

export const VOTE_POLL_REQUEST = 'VOTE_POLL_REQUEST';
export const VOTE_POLL_SUCCESS = 'VOTE_POLL_SUCCESS';
export const VOTE_POLL_FAILURE = 'VOTE_POLL_FAILURE';

export const FETCH_USER_VOTES_REQUEST = 'FETCH_USER_VOTES_REQUEST';
export const FETCH_USER_VOTES_SUCCESS = 'FETCH_USER_VOTES_SUCCESS';
export const FETCH_USER_VOTES_FAILURE = 'FETCH_USER_VOTES_FAILURE';

export const UPDATE_POLL_REQUEST = 'UPDATE_POLL_REQUEST';
export const UPDATE_POLL_SUCCESS = 'UPDATE_POLL_SUCCESS';
export const UPDATE_POLL_FAILURE = 'UPDATE_POLL_FAILURE';

export const CLOSE_POLL_REQUEST = 'CLOSE_POLL_REQUEST';
export const CLOSE_POLL_SUCCESS = 'CLOSE_POLL_SUCCESS';
export const CLOSE_POLL_FAILURE = 'CLOSE_POLL_FAILURE';

export const FETCH_POLL_DETAILS_REQUEST = 'FETCH_POLL_DETAILS_REQUEST';
export const FETCH_POLL_DETAILS_SUCCESS = 'FETCH_POLL_DETAILS_SUCCESS';
export const FETCH_POLL_DETAILS_FAILURE = 'FETCH_POLL_DETAILS_FAILURE';

// LocalStorage key for fallback if API fails
const LS_KEY = 'scholiq_polls';
const LS_VOTES_KEY = 'scholiq_poll_votes';

const persistLocal = (polls) => {
    try { localStorage.setItem(LS_KEY, JSON.stringify(polls)); } catch(e) { /* noop */ }
};
const readLocal = () => {
    try { return JSON.parse(localStorage.getItem(LS_KEY) || '[]'); } catch(e){ return []; }
};

// Track user votes to prevent duplicate voting (local fallback)
const getUserVotesFromLocal = () => {
    try { return JSON.parse(localStorage.getItem(LS_VOTES_KEY) || '{}'); } catch(e) { return {}; }
};

const saveUserVoteToLocal = (pollId, optionId) => {
    try {
        const votes = getUserVotesFromLocal();
        votes[pollId] = optionId;
        localStorage.setItem(LS_VOTES_KEY, JSON.stringify(votes));
    } catch(e) { /* noop */ }
};

// State management
let cachedUserVotes = null;

// Fetch Polls with pagination support
export const fetchPolls = (page = 1, limit = 20, useCache = true) => async (dispatch) => {
    dispatch({ type: FETCH_POLLS_REQUEST });
    try {
        const polls = await pollService.getAllPolls(page, limit, useCache);
        dispatch({ type: FETCH_POLLS_SUCCESS, payload: polls });
        persistLocal(polls); // Keep local copy as backup
    } catch (err) {
        console.error('Error fetching polls:', err);
        // Fallback to local storage
        const fallback = readLocal();
        dispatch({ type: FETCH_POLLS_FAILURE, error: err.message, fallback });
    }
};

// Create Poll
export const createPoll = (poll) => async (dispatch) => {
    dispatch({ type: CREATE_POLL_REQUEST });
    try {
        const createdPoll = await pollService.createPoll(poll);
        dispatch({ type: CREATE_POLL_SUCCESS, payload: createdPoll });
        // Refetch polls to ensure we have the latest data
        dispatch(fetchPolls());
    } catch (err) {
        console.error('Error creating poll:', err);
        dispatch({ type: CREATE_POLL_FAILURE, error: err.message });
        // Show error notification or handle the error appropriately
    }
};

// Delete Poll
export const deletePoll = (id) => async (dispatch) => {
    dispatch({ type: DELETE_POLL_REQUEST, meta: { id } });
    try {
        await pollService.deletePoll(id);
        dispatch({ type: DELETE_POLL_SUCCESS, payload: id });
        // Refetch polls to ensure we have the latest data
        dispatch(fetchPolls());
    } catch (err) {
        console.error('Error deleting poll:', err);
        dispatch({ type: DELETE_POLL_FAILURE, error: err.message });
        // Show error notification or handle the error appropriately
    }
};

// Vote on Poll
export const votePoll = (pollId, optionId) => async (dispatch) => {
    dispatch({ type: VOTE_POLL_REQUEST });
    try {
        await pollService.votePoll(pollId, optionId);
        dispatch({
            type: VOTE_POLL_SUCCESS,
            payload: { pollId, optionId }
        });

        // Update local cache of user votes
        if (cachedUserVotes) {
            cachedUserVotes[pollId] = optionId;
        }

        // Also save to local storage as backup
        saveUserVoteToLocal(pollId, optionId);

        // Refetch polls to get updated vote counts
        dispatch(fetchPolls());
    } catch (err) {
        console.error('Error voting on poll:', err);
        dispatch({ type: VOTE_POLL_FAILURE, error: err.message });
        // Show error notification or handle the error appropriately
    }
};

// Fetch user votes
export const fetchUserVotes = () => async (dispatch) => {
    dispatch({ type: FETCH_USER_VOTES_REQUEST });
    try {
        const userVotes = await pollService.getUserVotes();
        cachedUserVotes = userVotes;
        dispatch({ type: FETCH_USER_VOTES_SUCCESS, payload: userVotes });
    } catch (err) {
        console.error('Error fetching user votes:', err);
        // Fallback to local storage
        const localVotes = getUserVotesFromLocal();
        cachedUserVotes = localVotes;
        dispatch({ type: FETCH_USER_VOTES_FAILURE, error: err.message, fallback: localVotes });
    }
};

// Update an existing poll
export const updatePoll = (pollId, pollData) => async (dispatch) => {
    dispatch({ type: UPDATE_POLL_REQUEST });
    try {
        const updatedPoll = await pollService.updatePoll(pollId, pollData);
        dispatch({ type: UPDATE_POLL_SUCCESS, payload: updatedPoll });
        // Refetch polls to ensure we have the latest data
        dispatch(fetchPolls());
    } catch (err) {
        console.error('Error updating poll:', err);
        dispatch({ type: UPDATE_POLL_FAILURE, error: err.message });
    }
};

// Close a poll (prevent further voting)
export const closePoll = (pollId) => async (dispatch) => {
    dispatch({ type: CLOSE_POLL_REQUEST });
    try {
        const closedPoll = await pollService.closePoll(pollId);
        dispatch({ type: CLOSE_POLL_SUCCESS, payload: closedPoll });
        // Refetch polls to ensure we have the latest data
        dispatch(fetchPolls());
    } catch (err) {
        console.error('Error closing poll:', err);
        dispatch({ type: CLOSE_POLL_FAILURE, error: err.message });
    }
};

// Fetch specific poll details
export const fetchPollDetails = (pollId, useCache = true) => async (dispatch) => {
    dispatch({ type: FETCH_POLL_DETAILS_REQUEST });
    try {
        const pollDetails = await pollService.getPollDetails(pollId, useCache);
        dispatch({ type: FETCH_POLL_DETAILS_SUCCESS, payload: pollDetails });
    } catch (err) {
        console.error('Error fetching poll details:', err);
        dispatch({ type: FETCH_POLL_DETAILS_FAILURE, error: err.message });
    }
};

// Force refresh polls cache
export const refreshPolls = () => async (dispatch) => {
    pollService.invalidateCache('polls');
    dispatch(fetchPolls(1, 20, false)); // Force fetch without cache
};

// Force refresh user votes cache
export const refreshUserVotes = () => async (dispatch) => {
    pollService.invalidateCache('userVotes');
    dispatch(fetchUserVotes(false)); // Force fetch without cache
};

// Helper function to check if user has already voted on a poll
export const hasUserVoted = (pollId) => {
    // First try from cache
    if (cachedUserVotes) {
        return cachedUserVotes[pollId] !== undefined;
    }

    // Fallback to localStorage if API data not available
    const votes = getUserVotesFromLocal();
    return votes[pollId] !== undefined;
};

// Helper function to get the option ID the user voted for
export const getUserVote = (pollId) => {
    // First try from cache
    if (cachedUserVotes) {
        return cachedUserVotes[pollId];
    }

    // Fallback to localStorage if API data not available
    const votes = getUserVotesFromLocal();
    return votes[pollId];
};
